/**
 * useReportGenerator
 *
 * Generates a structured learning progress report for a student.
 * The voice (first / third person) is determined by the reader's user type:
 *
 *  - student  → first person  ("You have demonstrated...", "Focus on...")
 *  - parent   → warm third    ("Adaeze has shown...", "We recommend she...")
 *  - school   → professional  ("The student demonstrates...", "It is recommended that...")
 *  - super_admin → neutral    (same as school)
 */

import type { StudentSummary } from './useStudentSummary';
import type { StudentAnalytics } from './useStudentAnalytics';

export type ReaderType = 'student' | 'parent' | 'school' | 'super_admin'

export interface ReportSection {
  heading: string;
  paragraphs: string[];
}

export interface LearningReport {
  title: string;
  subtitle: string;
  generatedAt: string;         // formatted date string
  studentName: string;
  classLevel: string;
  examBody: string;
  sections: ReportSection[];
}

// ─── Pronoun helpers ──────────────────────────────────────────────────────────

interface Voice {
  subject: string;      // "You" / "Adaeze" / "The student"
  possessive: string;   // "Your" / "Adaeze's" / "The student's"
  object: string;       // "you" / "Adaeze" / "the student"
  reflexive: string;    // "yourself" / "herself" / "themselves"
  verb2: string;        // "have" / "has" / "has"  (auxiliary)
  genderPossessive: string; // "your" / "her/his" / "their"
  imperative: string;   // "Focus" / "Adaeze should focus" / "It is recommended that the student focus"
}

function buildVoice(readerType: ReaderType, name: string, gender?: string): Voice {
  const gp = gender === 'female' ? 'her' : gender === 'male' ? 'his' : 'their'

  switch (readerType) {
    case 'student':
      return {
        subject: 'You', possessive: 'Your', object: 'you',
        reflexive: 'yourself', verb2: 'have', genderPossessive: 'your',
        imperative: '',   // direct: "Focus on..." no prefix
      }
    case 'parent':
      return {
        subject: name, possessive: `${name}'s`, object: name,
        reflexive: `${name}self`, verb2: 'has', genderPossessive: gp,
        imperative: `${name} should`,
      }
    default: // school / super_admin
      return {
        subject: 'The student', possessive: "The student's", object: 'the student',
        reflexive: 'themselves', verb2: 'has', genderPossessive: 'their',
        imperative: `It is recommended that ${name}`,
      }
  }
}

// ─── Career path focus advice ─────────────────────────────────────────────────

const PATH_FOCUS: Record<string, string[]> = {
  broad_stem_tech: [
    'strengthen mathematical reasoning and logical problem solving',
    'build foundational programming knowledge through structured practice',
    'explore applied science projects to link classroom theory to real-world applications',
  ],
  broad_health_medicine: [
    'deepen understanding of biological systems and chemistry',
    'develop empathy and communication skills — critical for patient-facing roles',
    'pursue first-aid or community health volunteering for early practical exposure',
  ],
  broad_creative_arts: [
    'build a portfolio of original work to document creative growth',
    'study the business side of creative industries alongside practice',
    'seek collaboration opportunities to expand creative range',
  ],
  broad_business_finance: [
    'develop numerical literacy and financial reasoning',
    'study real companies and markets to connect theory to practice',
    'build entrepreneurial thinking through small projects or competitions',
  ],
  broad_law_social: [
    'practise structured argumentation and critical reading',
    'engage with current affairs to understand law and society in context',
    'develop strong written communication skills through essays and debates',
  ],
}

function getPathFocus(pathId: string): string[] {
  const key = Object.keys(PATH_FOCUS).find((k) => pathId.includes(k.replace('broad_', '')))
  return PATH_FOCUS[key ?? ''] ?? [
    'continue engaging consistently with the curriculum',
    'ask targeted questions during lessons to deepen understanding',
    'connect classroom learning to real-world examples where possible',
  ]
}

// ─── Score band helper ────────────────────────────────────────────────────────

function scoreBand(score: number): 'strong' | 'developing' | 'needs attention' {
  if (score >= 75) return 'strong'
  if (score >= 50) return 'developing'
  return 'needs attention'
}

// ─── Main generator ───────────────────────────────────────────────────────────

export function generateReport(
  summary: StudentSummary,
  analytics: StudentAnalytics,
  readerType: ReaderType,
  gender?: string,
): LearningReport {
  const v = buildVoice(readerType, summary.name, gender)
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  // ── Section 1: Overview ───────────────────────────────────────────────────
  const completedCount = analytics.careerPathProgress.reduce(
    (s, p) => s + Math.round(p.progress / 10), 0,
  )
  const pathNames = analytics.careerPathProgress.map((p) => p.pathName).join(' and ') || 'no career path yet'

  const overview: ReportSection = {
    heading: 'Overview',
    paragraphs: [
      `${v.subject} ${v.verb2} been assessed at ${summary.classLevel} level under the ${summary.examBody} system${summary.country !== 'Unknown' ? ` (${summary.country})` : ''}. ` +
      `${v.possessive} current career exploration focuses on: ${pathNames}.`,

      `${v.subject} ${v.verb2} completed an estimated ${completedCount} topic unit${completedCount !== 1 ? 's' : ''} ` +
      `and ${v.verb2} ${analytics.goalProgress.completed} goal${analytics.goalProgress.completed !== 1 ? 's' : ''} marked as achieved, ` +
      `with ${analytics.goalProgress.active} still in progress.`,
    ],
  }

  // ── Section 2: Strengths ──────────────────────────────────────────────────
  const topTraitList = summary.topTraits.slice(0, 3)
  const traitDescriptions = topTraitList.map((t) => {
    const band = scoreBand(t.score)
    return `${t.name} (${band === 'strong' ? 'strong' : band === 'developing' ? 'growing' : 'emerging'})`
  }).join(', ')

  const subjectHighlights = analytics.subjectStrengths
    .filter((s) => s.score >= 70)
    .slice(0, 2)
    .map((s) => `${s.subject} (avg. ${s.score}%)`)
    .join(' and ')

  const strengths: ReportSection = {
    heading: 'Strengths',
    paragraphs: [
      topTraitList.length > 0
        ? `${v.possessive} psychometric profile highlights the following traits: ${traitDescriptions}. ` +
          `These are directly aligned with the demands of ${v.genderPossessive} chosen career path${analytics.careerPathProgress.length !== 1 ? 's' : ''}.`
        : `${v.subject} ${v.verb2} not yet completed the psychometric assessment. Completing it will unlock a detailed trait profile.`,

      subjectHighlights
        ? `${v.subject} ${v.verb2} demonstrated above-average performance in ${subjectHighlights}, ` +
          `which provides a strong academic foundation to build on.`
        : `Academic performance data will appear as ${v.object} completes quizzes and activities.`,
    ],
  }

  // ── Section 3: Areas for Improvement ────────────────────────────────────
  const weakTraitList = summary.weakTraits.slice(0, 2)
  const weakSubjects = analytics.subjectStrengths
    .filter((s) => s.score < 60)
    .slice(0, 2)
    .map((s) => s.subject)
    .join(' and ')

  const improvement: ReportSection = {
    heading: 'Areas for Development',
    paragraphs: [
      weakTraitList.length > 0
        ? `Traits that show room for growth include ${weakTraitList.map((t) => t.name).join(' and ')}. ` +
          `These can be developed through targeted activities — the platform's activity logger and recommended exercises are great starting points.`
        : `No significant weak areas have been identified yet. Continued engagement across all subject areas will help maintain balance.`,

      weakSubjects
        ? `Subject areas with lower performance include ${weakSubjects}. ` +
          (readerType === 'student'
            ? `Revisiting these topics using the Journey page — especially the AI-generated quizzes — can help build confidence quickly.`
            : `Revisiting these topics through the platform's Journey page and supplementary resources is recommended.`)
        : `Subject performance across all assessed areas is satisfactory at this stage.`,

      analytics.goalProgress.overdue > 0
        ? `There ${analytics.goalProgress.overdue === 1 ? 'is' : 'are'} currently ${analytics.goalProgress.overdue} overdue goal${analytics.goalProgress.overdue !== 1 ? 's' : ''}. ` +
          (readerType === 'student'
            ? `Reviewing and re-scoping these goals will help keep your momentum going.`
            : `Reviewing and adjusting these goals with appropriate timelines is recommended to maintain motivation.`)
        : '',
    ].filter(Boolean),
  }

  // ── Section 4: Career Path Recommendations ───────────────────────────────
  const pathSections: string[] = []
  for (const path of analytics.careerPathProgress.slice(0, 2)) {
    const pct = path.progress
    const focusPoints = getPathFocus(path.pathId)
    const progressNote =
      pct === 0 ? `has not yet started the ${path.pathName} path`
      : pct < 30 ? `is in the early stages of the ${path.pathName} path (${pct}% complete)`
      : pct < 70 ? `is making good progress through the ${path.pathName} path (${pct}%)`
      : `is in the advanced stages of the ${path.pathName} path (${pct}%)`

    pathSections.push(
      `${v.subject} ${progressNote}. ` +
      `To advance, ${v.imperative ? v.imperative.toLowerCase() : 'the focus should be to'} ` +
      `${focusPoints[0]}, and ${focusPoints[1] ?? 'continue building on existing strengths'}.`,
    )
  }

  if (pathSections.length === 0) {
    pathSections.push(
      `${v.subject} ${v.verb2} not yet enrolled in a career path. ` +
      (readerType === 'student'
        ? `Head to the Career Explorer to find a path that matches your interests and traits.`
        : `Exploring and enrolling in a career path is the recommended next step.`),
    )
  }

  const careerRecs: ReportSection = {
    heading: 'Career Path Recommendations',
    paragraphs: pathSections,
  }

  // ── Section 5: Next Steps ────────────────────────────────────────────────
  const nextSteps: string[] = []

  if (!summary.topTraits.length) {
    nextSteps.push(
      readerType === 'student'
        ? 'Complete the psychometric assessment — it takes about 15 minutes and unlocks your full trait profile and career suggestions.'
        : `Completing the psychometric assessment should be prioritised — it unlocks ${v.genderPossessive} full trait profile.`,
    )
  }

  if (analytics.goalProgress.active === 0 && analytics.goalProgress.completed === 0) {
    nextSteps.push(
      readerType === 'student'
        ? 'Set your first learning goal on the Goals page. Even a small, specific goal creates focus and motivation.'
        : `Setting an initial learning goal on the Goals page will help establish direction and accountability.`,
    )
  }

  if (analytics.engagementTrend.reduce((s, e) => s + e.sessions, 0) < 3) {
    nextSteps.push(
      readerType === 'student'
        ? 'Aim for at least 3 study sessions per week. Consistency matters more than long single sessions.'
        : `Building a habit of at least 3 study sessions per week is recommended for steady progress.`,
    )
  }

  const topPath = analytics.careerPathProgress[0]
  if (topPath && topPath.progress < 50) {
    const nextFocus = getPathFocus(topPath.pathId)[2] ?? 'continue with the next lesson in the journey'
    nextSteps.push(
      readerType === 'student'
        ? `For your ${topPath.pathName} path: ${nextFocus}.`
        : `For the ${topPath.pathName} path: ${nextFocus}.`,
    )
  }

  if (nextSteps.length === 0) {
    nextSteps.push(
      readerType === 'student'
        ? 'Keep up the great work! Continue exploring your career paths and logging your activities to track your growth.'
        : `${v.subject} is progressing well. Continued engagement with the platform will sustain this momentum.`,
    )
  }

  const next: ReportSection = {
    heading: 'Recommended Next Steps',
    paragraphs: nextSteps,
  }

  // ── Closing note ──────────────────────────────────────────────────────────
  const closing: ReportSection = {
    heading: 'Closing Note',
    paragraphs: [
      readerType === 'student'
        ? `Remember that every expert started exactly where you are now. The fact that you are tracking your progress and reflecting on your learning already puts you ahead. Stay consistent, ask questions, and trust the process.`
        : readerType === 'parent'
        ? `${summary.name} is on a journey of discovery. The most powerful thing you can do as a parent is encourage consistency, celebrate small wins, and remind ${v.object} that growth takes time. This report is a snapshot — the real story is still being written.`
        : `This report reflects the student's current state as recorded on the platform. For a fuller picture, cross-reference with teacher observations and recent academic results. The platform's data will grow richer as the student logs more activities and completes more assessments.`,
    ],
  }

  return {
    title: 'Learning Progress Report',
    subtitle: readerType === 'student' ? 'Your Personal Report' : readerType === 'parent' ? `Progress Report for ${summary.name}` : `Student Report — ${summary.name}`,
    generatedAt: dateStr,
    studentName: summary.name,
    classLevel: summary.classLevel,
    examBody: summary.examBody,
    sections: [overview, strengths, improvement, careerRecs, next, closing],
  }
}
